<template>
  <div>
    <EditorMenuBar
      v-slot="{isActive: ia, commands: cmds, getMarkAttrs: gma}"
      :editor="editor"
    >
      <div class="editor-menubar">
        <b-form-group>
          <div class="flex items-center justify-between">
            <label class="p-0">HTML Tag</label>
            
            <v-select
              :value="getBlockType()"
              :options="['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p']"
              :searchable="false"
              class="w-auto min-w-[90px]"
              @option:selected="val => cmd('setBlockType', { type: val })"
            />
          </div>
        </b-form-group>

        <sid-font
          :sid="`.--${section.sid}-text div`"
          :editor="true"
          :selected-font="gma('fontfamily')"
          :selected-font-weight="gma('fontweight')"
          @onFontChange="font => applyFontFamily(font, cmds, gma('fontfamily'))"
          @onFontWeightChange="weight => applyFontWeight(weight, cmds, gma('fontweight'))"
        />
        
        <sid-font-size
          label="Font Size"
          rule-name="font-size"
          :sid="`.--${section.sid}-text div`"
          :selected="gma('fontsize')"
          @onChange="val => applyFontSize(val, cmds, gma('fontsize'))"
          @onReset="val => resetFontSize(val, cmds, gma('fontsize'))"
        />

        <b-form-group>
          <div class="flex items-center justify-between">
            <label class="p-0">Text Alignment</label>

            <b-btn-group>
              <b-btn
                v-b-tooltip
                title="Left"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextAlignActive('left') }"
                @click="cmd('toggleLeft')"
              >
                <i
                  :class="isTextAlignActive('left') ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-align-left text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Center"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextAlignActive('center') }"
                @click="cmd('toggleCenter')"
              >
                <i
                  :class="isTextAlignActive('center') ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-align-center text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Right"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextAlignActive('right') }"
                @click="cmd('toggleRight')"
              >
                <i
                  :class="isTextAlignActive('right') ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-align-center text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Justify"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextAlignActive('justify') }"
                @click="cmd('toggleJustify')"
              >
                <i
                  :class="isTextAlignActive('justify') ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-align-justify text-xs"
                />
              </b-btn>
            </b-btn-group>
          </div>
        </b-form-group>

        <b-form-group>
          <div class="flex items-center justify-between">
            <label class="p-0">Text Transform</label>

            <b-btn-group>
              <b-btn
                v-b-tooltip
                title="Uppercase"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextTransformActive('uppercase') }"
                @click="cmd('toggleUppercase')"
              >
                <svg
                  width="21"
                  height="12"
                  viewBox="0 0 21 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="scale-75"
                  :class="isTextTransformActive('uppercase') ? 'text-dark' : 'text-[#AEB1B4]'"
                >
                  <path
                    d="M6.28906 8.22656L4.75781 4.52344H4.625L3.17969 8.22656H6.28906ZM0.289062 11.5938C0.674479 11.5677 0.984375 11.3932 1.21875 11.0703C1.36979 10.8672 1.58594 10.4193 1.86719 9.72656L5.42188 0.96875H5.86719L9.4375 9.375C9.83854 10.3177 10.138 10.9271 10.3359 11.2031C10.5339 11.474 10.8151 11.6042 11.1797 11.5938V12H6V11.5938C6.52083 11.5729 6.86198 11.5286 7.02344 11.4609C7.1901 11.3932 7.27344 11.2214 7.27344 10.9453C7.27344 10.8203 7.23177 10.6302 7.14844 10.375C7.09635 10.224 7.02604 10.0417 6.9375 9.82812L6.53125 8.85938H2.9375C2.70312 9.49479 2.55208 9.90885 2.48438 10.1016C2.34375 10.513 2.27344 10.8125 2.27344 11C2.27344 11.2292 2.42708 11.3958 2.73438 11.5C2.91667 11.5573 3.1901 11.5885 3.55469 11.5938V12H0.289062V11.5938Z"
                    fill="currentColor"
                  />
                  <path
                    d="M11.2578 11.5938C11.6901 11.5781 12 11.5208 12.1875 11.4219C12.5052 11.2552 12.6641 10.9375 12.6641 10.4688V2.72656C12.6641 2.25781 12.5156 1.94271 12.2188 1.78125C12.0417 1.68229 11.7214 1.61979 11.2578 1.59375V1.1875H16.2031C17.1562 1.1875 17.9479 1.29688 18.5781 1.51562C19.7656 1.92708 20.3594 2.67708 20.3594 3.76562C20.3594 4.42708 20.1172 4.95312 19.6328 5.34375C19.1536 5.72917 18.6328 5.97396 18.0703 6.07812V6.22656C18.6641 6.34115 19.1797 6.53385 19.6172 6.80469C20.4714 7.33594 20.8984 8.07552 20.8984 9.02344C20.8984 9.90885 20.4896 10.6276 19.6719 11.1797C18.8542 11.7266 17.7656 12 16.4062 12H11.2578V11.5938ZM15.2188 6.03906C16.2083 6.03906 16.8724 5.8776 17.2109 5.55469C17.5495 5.23177 17.7188 4.65885 17.7188 3.83594C17.7188 3.22656 17.6068 2.71354 17.3828 2.29688C17.1641 1.88021 16.7396 1.67188 16.1094 1.67188C15.776 1.67188 15.5443 1.72917 15.4141 1.84375C15.2839 1.95312 15.2188 2.15365 15.2188 2.44531V6.03906ZM15.2188 10.5234C15.224 10.8047 15.2656 11.0104 15.3438 11.1406C15.474 11.3698 15.724 11.4844 16.0938 11.4844C16.849 11.4844 17.3776 11.2786 17.6797 10.8672C17.987 10.4557 18.1406 9.86979 18.1406 9.10938C18.1406 7.97396 17.8047 7.21875 17.1328 6.84375C16.7266 6.61458 16.0885 6.50781 15.2188 6.52344V10.5234Z"
                    fill="currentColor"
                  />
                </svg>
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Capitalize"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextTransformActive('capitalize')}"
                @click="cmd('toggleCapitalize')"
              >
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="scale-75"
                  :class="isTextTransformActive('capitalize') ? 'text-dark' : 'text-[#AEB1B4]'"
                >
                  <path
                    d="M6.28906 10.2266L4.75781 6.52344H4.625L3.17969 10.2266H6.28906ZM0.289062 13.5938C0.674479 13.5677 0.984375 13.3932 1.21875 13.0703C1.36979 12.8672 1.58594 12.4193 1.86719 11.7266L5.42188 2.96875H5.86719L9.4375 11.375C9.83854 12.3177 10.138 12.9271 10.3359 13.2031C10.5339 13.474 10.8151 13.6042 11.1797 13.5938V14H6V13.5938C6.52083 13.5729 6.86198 13.5286 7.02344 13.4609C7.1901 13.3932 7.27344 13.2214 7.27344 12.9453C7.27344 12.8203 7.23177 12.6302 7.14844 12.375C7.09635 12.224 7.02604 12.0417 6.9375 11.8281L6.53125 10.8594H2.9375C2.70312 11.4948 2.55208 11.9089 2.48438 12.1016C2.34375 12.513 2.27344 12.8125 2.27344 13C2.27344 13.2292 2.42708 13.3958 2.73438 13.5C2.91667 13.5573 3.1901 13.5885 3.55469 13.5938V14H0.289062V13.5938Z"
                    fill="currentColor"
                  />
                  <path
                    d="M14.3672 12.4453C14.388 12.737 14.4453 12.9766 14.5391 13.1641C14.7266 13.5286 15.0391 13.7109 15.4766 13.7109C16.0234 13.7109 16.4089 13.4219 16.6328 12.8438C16.862 12.2656 16.9766 11.4479 16.9766 10.3906C16.9766 9.55208 16.875 8.83073 16.6719 8.22656C16.474 7.6224 16.0833 7.32031 15.5 7.32031C15.1562 7.32031 14.8802 7.44792 14.6719 7.70312C14.4688 7.95312 14.3672 8.13281 14.3672 8.24219V12.4453ZM14.3672 3.1875V7.33594C14.6172 7.09115 14.849 6.90625 15.0625 6.78125C15.4531 6.55729 15.875 6.44531 16.3281 6.44531C17.2292 6.44531 17.9531 6.79688 18.5 7.5C19.0521 8.20312 19.3281 9.06771 19.3281 10.0938C19.3281 11.4167 18.9766 12.4375 18.2734 13.1562C17.5755 13.8698 16.7057 14.2266 15.6641 14.2266C15.237 14.2266 14.8594 14.1432 14.5312 13.9766C14.2031 13.8099 13.8958 13.5911 13.6094 13.3203L12.3516 14.2266H12.125V4.5C12.125 4.16667 12.0651 3.94271 11.9453 3.82812C11.8255 3.70833 11.6016 3.625 11.2734 3.57812V3.1875H14.3672Z"
                    fill="currentColor"
                  />
                </svg>
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Lowercase"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': isTextTransformActive('lowercase')}"
                @click="cmd('toggleLowercase')"
              >
                <svg
                  width="17"
                  height="12"
                  viewBox="0 0 17 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="scale-75"
                  :class="isTextTransformActive('lowercase') ? 'text-dark' : 'text-[#AEB1B4]'"
                >
                  <path
                    d="M3.50781 10.0859C3.76823 10.0859 4.01042 10.0078 4.23438 9.85156C4.35938 9.75781 4.50781 9.61198 4.67969 9.41406V7.07812C4.2526 7.18229 3.8776 7.34635 3.55469 7.57031C3.00781 7.95052 2.73438 8.4349 2.73438 9.02344C2.73438 9.3724 2.8125 9.63802 2.96875 9.82031C3.13021 9.9974 3.3099 10.0859 3.50781 10.0859ZM0.40625 9.50781C0.40625 8.78906 0.752604 8.21875 1.44531 7.79688C2.13802 7.375 3.22396 6.95052 4.70312 6.52344V5.35938C4.70312 4.88542 4.60156 4.53646 4.39844 4.3125C4.20052 4.08333 3.84635 3.96875 3.33594 3.96875C3.04948 3.96875 2.8125 4.02344 2.625 4.13281C2.44271 4.23698 2.35156 4.36979 2.35156 4.53125C2.35156 4.59375 2.36198 4.64583 2.38281 4.6875C2.40365 4.72917 2.4349 4.77344 2.47656 4.82031L2.57031 4.92969C2.64323 5.01823 2.70052 5.11719 2.74219 5.22656C2.78906 5.33073 2.8125 5.44271 2.8125 5.5625C2.8125 5.86458 2.71094 6.09896 2.50781 6.26562C2.3099 6.42708 2.07812 6.50781 1.8125 6.50781C1.50521 6.50781 1.24219 6.41667 1.02344 6.23438C0.809896 6.05208 0.703125 5.78906 0.703125 5.44531C0.703125 4.83073 0.992188 4.34375 1.57031 3.98438C2.14844 3.625 2.85938 3.44531 3.70312 3.44531C4.60938 3.44531 5.36719 3.61979 5.97656 3.96875C6.58073 4.32292 6.88281 4.97396 6.88281 5.92188V9.79688C6.88281 9.91146 6.91146 10.0104 6.96875 10.0938C7.03125 10.1719 7.09896 10.2109 7.17188 10.2109C7.23438 10.2109 7.28906 10.2005 7.33594 10.1797C7.38802 10.1536 7.46615 10.0885 7.57031 9.98438L7.8125 10.3203C7.5 10.6849 7.15365 10.9401 6.77344 11.0859C6.53385 11.1797 6.28125 11.2266 6.01562 11.2266C5.54167 11.2266 5.19531 11.0885 4.97656 10.8125C4.85156 10.6562 4.76042 10.4245 4.70312 10.1172C4.26042 10.5599 3.76042 10.8724 3.20312 11.0547C2.86458 11.1693 2.53125 11.2266 2.20312 11.2266C1.76562 11.2266 1.35677 11.0885 0.976562 10.8125C0.596354 10.5312 0.40625 10.0964 0.40625 9.50781Z"
                    fill="currentColor"
                  />
                  <path
                    d="M11.3672 9.44531C11.388 9.73698 11.4453 9.97656 11.5391 10.1641C11.7266 10.5286 12.0391 10.7109 12.4766 10.7109C13.0234 10.7109 13.4089 10.4219 13.6328 9.84375C13.862 9.26562 13.9766 8.44792 13.9766 7.39062C13.9766 6.55208 13.875 5.83073 13.6719 5.22656C13.474 4.6224 13.0833 4.32031 12.5 4.32031C12.1562 4.32031 11.8802 4.44792 11.6719 4.70312C11.4688 4.95312 11.3672 5.13281 11.3672 5.24219V9.44531ZM11.3672 0.1875V4.33594C11.6172 4.09115 11.849 3.90625 12.0625 3.78125C12.4531 3.55729 12.875 3.44531 13.3281 3.44531C14.2292 3.44531 14.9531 3.79688 15.5 4.5C16.0521 5.20312 16.3281 6.06771 16.3281 7.09375C16.3281 8.41667 15.9766 9.4375 15.2734 10.1562C14.5755 10.8698 13.7057 11.2266 12.6641 11.2266C12.237 11.2266 11.8594 11.1432 11.5312 10.9766C11.2031 10.8099 10.8958 10.5911 10.6094 10.3203L9.35156 11.2266H9.125V1.5C9.125 1.16667 9.0651 0.942708 8.94531 0.828125C8.82552 0.708333 8.60156 0.625 8.27344 0.578125V0.1875H11.3672Z"
                    fill="currentColor"
                  />
                </svg>
              </b-btn>
            </b-btn-group>
          </div>
        </b-form-group>

        <b-form-group>
          <div class="flex items-center justify-between">
            <label class="p-0">Text Decoration</label>

            <b-btn-group>
              <b-btn
                v-b-tooltip
                title="Bold"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.bold()}"
                @click="cmd('toggleBold')"
              >
                <i
                  :class="ia.bold() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-bold text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Italic"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.italic()}"
                @click="cmd('toggleItalic')"
              >
                <i
                  :class="ia.italic() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-italic text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Underline"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.underline()}"
                @click="cmd('toggleUnderline')"
              >
                <i
                  :class="ia.underline() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-underline text-xs"
                />
              </b-btn>
              <b-btn
                v-b-tooltip
                title="Strikethrough"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.strike()}"
                @click="cmd('toggleStrike')"
              >
                <i
                  :class="ia.strike() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-strikethrough text-xs"
                />
              </b-btn>
            </b-btn-group>
          </div>
        </b-form-group>

        <b-form-group>
          <div class="flex items-center justify-between">
            <label class="p-0">List</label>

            <b-btn-group>
              <b-btn
                v-b-tooltip
                title="Bullet List"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.bullet_list()}"
                @click="cmd('toggleBulletList')"
              >
                <i
                  :class="ia.bullet_list() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-list-ul text-xs"
                />
              </b-btn>

              <b-btn
                v-b-tooltip
                title="Ordered List"
                variant="white"
                class="h-[28px] w-[28px] p-0"
                :class="{'is-active': ia.ordered_list()}"
                @click="cmd('toggleOrderedList')"
              >
                <i
                  :class="ia.ordered_list() ? 'text-dark' : 'text-[#AEB1B4]'"
                  class="fa fa-list-ol text-xs"
                />
              </b-btn>
            </b-btn-group>
          </div>
        </b-form-group>

        <b-form-group>
          <background-type
            :value="getPickedGradientColorAttributes(gma('textgradientcolor'))"
            :config="colorType"
            label="Color Type"
            @change="() => gma('textgradientcolor').raw ? backgroundGradient.backgroundGradient = JSON.parse(gma('textgradientcolor').raw) : null"
            @updated="val => textColorTypeChanged(cmds, gma, val)"
          />

          <div
            v-if="!colorType.backgroundType"
            class="flex justify-end"
          >
            <color-picker-placeholder
              ref="colorPickerInvoker"
              class="mr-2"
              :color="getPickedColorAttributes(gma('textcolor'))"
              @click.native="setCommands({commands: cmds, getMarkAttrs: gma})"
            />

            <span
              class="mt-2 block text-[#333333] underline text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
              @click="() => applyTextColor('', cmds)"
            >
              Reset color
            </span>
          </div>

          <!--          <div v-else>-->
          <!--            <gradient-background-->
          <!--              :config="backgroundGradient"-->
          <!--              direction-enabled-->
          <!--              @change="val => applyGradientTextColor(val, cmds, gma('textgradientcolor').raw)"-->
          <!--            />-->
          <!--          </div>-->
        </b-form-group>
      </div>
    </EditorMenuBar>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import SidFont from '@/components/editor/components/sid-controls/sid-control-pressets/font/SidFont.vue'
import SidFontSize from '@/components/editor/components/sid-controls/sid-control-pressets/font/SidFonSize.vue'
import ColorPickerPlaceholder from '@/components/editor/utils/ColorPickerPlaceholder.vue'
import BackgroundType from '@/components/editor/controls/BackgroundType.vue'

const EditorMenuBar = {
  name: 'EditorMenuBarCompat',
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  computed: {
    context () {
      const editor = this.editor

      if (!editor) {
        return null
      }

      const typeNameMap = {
        bullet_list: 'bulletList',
        ordered_list: 'orderedList'
      }

      const isActive = typeof Proxy !== 'undefined'
        ? new Proxy(
          {},
          {
            get (_target, name) {
              return (attrs) => {
                const typeName = typeNameMap[name] || name
                return editor.isActive(typeName, attrs)
              }
            }
          }
        )
        : {
          bold: (attrs) => editor.isActive('bold', attrs),
          italic: (attrs) => editor.isActive('italic', attrs),
          underline: (attrs) => editor.isActive('underline', attrs),
          strike: (attrs) => editor.isActive('strike', attrs),
          bullet_list: (attrs) => editor.isActive(typeNameMap.bullet_list, attrs),
          ordered_list: (attrs) => editor.isActive(typeNameMap.ordered_list, attrs)
        }

      const getAttrs = (name) => (editor ? editor.getAttributes(name) || {} : {})

      return {
        editor,
        commands: editor.commands,
        isActive,
        getMarkAttrs: getAttrs,
        getNodeAttrs: getAttrs
      }
    }
  },
  render () {
    if (!this.editor || !this.$scopedSlots.default || !this.context) {
      return null
    }

    return this.$scopedSlots.default(this.context)
  }
}

export default {
  name: 'TextEditorMod',
  components: {
    BackgroundType, ColorPickerPlaceholder,
    SidFontSize,
    SidFont,
    EditorMenuBar
  },
  props: {
    section: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    resolutioner: {
      type: String,
      default: 'lg'
    }
  },
  data () {
    return {
      tipa: null,
      colorType: {
        backgroundType: null
      },
      backgroundGradient: {
        backgroundGradient: [
          {
            'color': 'rgb(119,171,255)',
            'position': 0
          },
          {
            'color': 'rgb(0,60,152)',
            'position': 100
          }
        ]
      }
    }
  },
  computed: {
    ...mapState('editor', {
      editor: state => state.tiptap
    })
  },
  methods: {
    getBlockType () {
      if (!this.editor) return 'p'
      const { state } = this.editor
      const $pos = state.selection.$from
      // Walk up the tree to find the nearest block node (paragraph or heading)
      for (let d = $pos.depth; d >= 0; d--) {
        const node = $pos.node(d)
        if (!node) continue
        const name = node.type && node.type.name
        if (name === 'heading') {
          const level = node.attrs && node.attrs.level ? node.attrs.level : 1
          return `h${level}`
        }
        if (name === 'paragraph') return 'p'
      }
      return 'p'
    },
    getFont () {
      if (!this.editor) return ''
      const { state } = this.editor
      const { selection } = state
      const markType = state.schema.marks && state.schema.marks.fontfamily
      if (!markType) return ''

      // caret: use storedMarks or marks at cursor
      if (selection.empty) {
        const marks = state.storedMarks || selection.$from.marks()
        const found = marks.find(m => m.type === markType)
        return found ? (found.attrs.family || '') : ''
      }

      // range: if all text nodes share same family, return it; else ''
      let family = null
      state.doc.nodesBetween(selection.from, selection.to, node => {
        if (!node.isText) return
        const m = node.marks && node.marks.find(mm => mm.type === markType)
        const f = m ? (m.attrs.family || '') : ''
        if (family === null) family = f
        else if (family !== f) family = ''
      })
      return family === null ? '' : family
    },
    isMarkActive (name, type) {
      if (!this.editor) return false
      const { state } = this.editor
      const { selection } = state
      const markType = state.schema.marks && state.schema.marks[name]
      if (!markType) return false

      // Caret-only selection: respect storedMarks or marks at cursor
      if (selection.empty) {
        const marks = state.storedMarks || selection.$from.marks()
        return marks.some(m => m.type === markType && m.attrs && m.attrs.type === type)
      }

      // Range selection: any node in range having the mark counts as active
      let active = false
      state.doc.nodesBetween(selection.from, selection.to, node => {
        if (active || !node.marks) return
        active = node.marks.some(m => m.type === markType && m.attrs && m.attrs.type === type)
      })
      return active
    },
    isTextTransformActive (type) {
      if (!this.editor) return false
      const { state } = this.editor
      const { selection } = state
      const markType = state.schema.marks && state.schema.marks.text_transform
      if (!markType) return false

      // Caret-only selection: respect storedMarks or marks at cursor
      if (selection.empty) {
        const marks = state.storedMarks || selection.$from.marks()
        return marks.some(m => m.type === markType && m.attrs && m.attrs.type === type)
      }

      // Range selection: any node in range having the mark counts as active
      let active = false
      state.doc.nodesBetween(selection.from, selection.to, node => {
        if (active || !node.marks) return
        active = node.marks.some(m => m.type === markType && m.attrs && m.attrs.type === type)
      })
      return active
    },
    cmd (name, options = {}) {
      if (!this.editor) return
      // Handle setBlockType for block type switching
      if (name === 'setBlockType') {
        const { type } = arguments[1] || {}
        if (!type) return
        this.editor.focus()
        const t = String(type)
        if (t === 'p' || t === 'paragraph') {
          if (typeof this.editor.commands.paragraph === 'function') return this.editor.commands.paragraph()
        }
        const match = /^h([1-6])$/.exec(t)
        if (match) {
          const level = parseInt(match[1], 10)
          if (typeof this.editor.commands.heading === 'function') return this.editor.commands.heading({ level })
        }
        // fallback: ignore unknown types
        return
      }
      // Font family command support
      if (name === 'setFont') {
        const { font } = options || {}
        this.editor.focus()
        const { commands } = this.editor
        // clear when empty string/null
        if (!font) {
          if (commands.clearFontFamily) return commands.clearFontFamily()
          if (commands.fontFamily) return commands.fontFamily({ family: '' })
          return
        }
        if (commands.fontFamily) return commands.fontFamily({ family: font })
        if (commands.setFontFamily) return commands.setFontFamily(font)
        return
      }
      this.editor.focus()
      const { commands } = this.editor
      if (name === 'toggleBold') return commands.bold()
      if (name === 'toggleItalic') return commands.italic()
      if (name === 'toggleUnderline') return commands.underline()
      if (name === 'toggleStrike') return commands.strike()
      if (name === 'toggleBulletList') return commands.bullet_list()
      if (name === 'toggleOrderedList') return commands.ordered_list()
      if (name === 'setBlockType') {
        if (options.type === 'p' || options.type === 'paragraph') {
          if (typeof commands.paragraph === 'function') return commands.paragraph()
        } else if (options.type === 'h1') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 1 })
        } else if (options.type === 'h2') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 2 })
        } else if (options.type === 'h3') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 3 })
        } else if (options.type === 'h4') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 4 })
        } else if (options.type === 'h5') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 5 })
        } else if (options.type === 'h6') {
          if (typeof commands.heading === 'function') return commands.heading({ level: 6 })
        }
        // fallback: ignore unknown types
        return
      }
      if (name === 'setFont') {
        if (options.font) {
          if (typeof commands.fontFamily === 'function') return commands.fontFamily({ family: options.font })
          if (typeof commands.setFontFamily === 'function') return commands.setFontFamily(options.font)
        }
        // fallback: ignore unknown fonts
        return
      }
      if (name === 'toggleLeft') {
        // Always set left (default)
        if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: 'left' })
        if (typeof commands.alignLeft === 'function') return commands.alignLeft()
      }
      if (name === 'toggleCenter') {
        if (this.isTextAlignActive('center')) {
          if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: null })
          if (typeof commands.alignLeft === 'function') return commands.alignLeft()
        }
        if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: 'center' })
        if (typeof commands.alignCenter === 'function') return commands.alignCenter()
      }
      if (name === 'toggleRight') {
        if (this.isTextAlignActive('right')) {
          if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: null })
          if (typeof commands.alignLeft === 'function') return commands.alignLeft()
        }
        if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: 'right' })
        if (typeof commands.alignRight === 'function') return commands.alignRight()
      }
      if (name === 'toggleJustify') {
        if (this.isTextAlignActive('justify')) {
          if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: null })
          if (typeof commands.alignLeft === 'function') return commands.alignLeft()
        }
        if (typeof commands.textAlign === 'function') return commands.textAlign({ textAlign: 'justify' })
        if (typeof commands.alignJustify === 'function') return commands.alignJustify()
      }
      if (name === 'toggleUppercase') {
        if (typeof commands.textTransform === 'function') return commands.textTransform({ textTransform: 'uppercase' })
        if (typeof commands.uppercase === 'function') return commands.uppercase()
      }
      if (name === 'toggleCapitalize') {
        if (typeof commands.textTransform === 'function') return commands.textTransform({ textTransform: 'capitalize' })
        if (typeof commands.capitalize === 'function') return commands.capitalize()
      }
      if (name === 'toggleLowercase') {
        if (typeof commands.textTransform === 'function') return commands.textTransform({ textTransform: 'lowercase' })
        if (typeof commands.lowercase === 'function') return commands.lowercase()
      }
    },
    isTextAlignActive (type) {
      if (!this.editor) return false
      const { state } = this.editor
      const { from, to, empty } = state.selection
      const types = ['paragraph', 'heading']
      const hasAlign = node => node && node.attrs && node.attrs.textAlign === type

      if (empty) {
        const $pos = state.selection.$from
        for (let d = $pos.depth; d > 0; d--) {
          const node = $pos.node(d)
          if (types.includes(node.type.name)) return hasAlign(node)
        }
        return false
      }

      let active = false
      state.doc.nodesBetween(from, to, node => {
        if (active) return
        if (types.includes(node.type.name)) {
          active = hasAlign(node)
        }
      })
      return active
    },
    applyFontFamily (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.fontFamily || !empty) {
        return setTimeout(() => commands.fontfamily({fontFamily: val.val}), 100)
      }

      val.apply()
    },
    applyFontWeight (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.fontWeight || !empty) {
        return setTimeout(() => commands.fontweight({fontWeight: val.val}), 100)
      }

      val.apply()
    },
    applyFontSize (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.sid || !empty) {
        commands.setFontSize({sid: val.sid})
        setTimeout(() => val.apply(), 100)
        return
      }

      val.apply()
    },
    resetFontSize (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.sid || !empty) {
        val.reset()
        setTimeout(() => commands.removeFontSize(), 100)
        return
      }

      val.reset()
    },
    getPickedGradientColorAttributes (attrs) {
      const bgGradient = this.config.resolutionStyle[this.resolutioner].hasOwnProperty('backgroundGradient') ? this.config.resolutionStyle[this.resolutioner].backgroundGradient : this.config.resolutionStyle['lg'].backgroundGradient
      this.colorType.backgroundType = attrs.raw || bgGradient ? 'gradient' : null

      if (!attrs.raw && this.config.resolutionStyle[this.resolutioner].backgroundGradient) {
        this.backgroundGradient.backgroundGradient = this.config.resolutionStyle[this.resolutioner].backgroundGradient
      }

      return JSON.stringify(attrs)
    },
    textColorTypeChanged (commands, getMarkAttrs, val) {
      const {state} = this.editor
      const empty = state.selection.empty

      setTimeout(() => {
        if (val.value) {
          if (!empty && !getMarkAttrs('textgradientcolor').raw) {
            this.applyGradientTextColor(this.backgroundGradient, commands)
            setTimeout(() => {
              // window.getSelection()?.removeAllRanges()
            }, 200)
            return
          }

          if (empty && !this.config.resolutionStyle[this.resolutioner].backgroundGradient) {
            this.applyGradientTextColor(this.backgroundGradient, commands)
          }
        } else {
          if (!empty && getMarkAttrs('textgradientcolor').raw) {
            this.applyGradientTextColor({backgroundGradient: null}, commands)
            setTimeout(() => {
              // window.getSelection()?.removeAllRanges()
            }, 200)
            return
          }

          if (empty) {
            return this.applyGradientTextColor({backgroundGradient: null}, commands)
          }
        }
      })
    },
    getPickedColorAttributes (attrs) {
      console.log(attrs)
      this.pickedColor = attrs.color || window.getComputedStyle(this.editor.view.dom).color
      return this.pickedColor
    },
    setCommands (commands) {
      this.tipa = commands
    },
    applyTextColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty
      this.pickedColor = val

      if (empty) {
        this.$refs['text-color'].val = val
      } else {
        commands.textcolor({color: val})
      }
    },
    applyGradientTextColor ({backgroundGradient, gradientDirection}, commands, selection = false) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (empty && !selection) {
        this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradient', backgroundGradient)
        this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradientDirection', gradientDirection)
      } else {
        commands.textgradientcolor({backgroundGradient, gradientDirection})
        window.getSelection()?.removeAllRanges()
      }
    },
    applyBackgroundColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (!empty) {
        commands.backgroundColor({backgroundColor: val})
      }
    }
  }
}
</script>
